<template>
  <header
    class="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-gray-100 shadow-sm"
  >
    <!-- Mobile menu button (hidden on desktop) -->
    <div class="md:hidden flex items-center justify-between px-4 py-3">
      <div class="flex items-center w-full justify-between">
        <button
          @click="mobileMenuOpen = !mobileMenuOpen"
          class="p-2 rounded-md text-gray-700 hover:text-indigo-700 hover:bg-gray-100 focus:outline-none"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-6 w-6"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M4 6h16M4 12h16M4 18h16"
            />
          </svg>
        </button>
        <!-- Mobile Search Bar -->
        



        <router-link to="/" class="flex items-center space-x-2 group">
          <img
            :src="`https://admin.from-malk.com/storage/${settings.logo}`"
            alt="Logo"
            class="h-10 w-auto object-contain transition-transform duration-300 group-hover:rotate-6"
          />
        </router-link>
      </div>

      <div class="flex items-center space-x-3">
        <!-- <button
          class="relative p-2 rounded-full hover:bg-gray-100 transition-colors group"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-5 w-5 text-gray-600 group-hover:text-indigo-700"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
        </button> -->

        <router-link
          to="/wishlist"
          class="relative p-2 rounded-full hover:bg-gray-100 transition-colors group"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-5 w-5 text-gray-600 group-hover:text-indigo-700"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
            />
          </svg>
          <span
            class="absolute -top-1 -right-1 text-xs bg-indigo-700 text-white rounded-full w-5 h-5 flex items-center justify-center shadow-md"
          >
            {{ cartStore.wishlistItems.length }}
          </span>
        </router-link>
      </div>
    </div>

    <!-- Desktop header (hidden on mobile) -->
    <div class="hidden md:block">
      <!-- Top section -->
      <div
        class="container mx-auto px-4 py-3 flex items-center justify-between"
      >
        <!-- Logo with gradient -->
        <router-link to="/" class="flex items-center space-x-2 group">
          <img
            :src="`https://admin.from-malk.com/storage/${settings.logo}`"
            alt="Logo"
            class="h-12 w-auto object-contain transition-transform duration-300 group-hover:rotate-6"
          />
        </router-link>

        <!-- Search Bar with unified design -->
        <div class="flex-1 mx-8 max-w-xl relative">
          <div class="relative flex items-center">
            <input
              v-model="searchInput"
              @keyup.enter="performSearch"
              type="text"
              :placeholder="$t('nav.searchForProducts')"
              class="w-full px-5 py-3 pr-12 border border-gray-300 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 rounded-full shadow-sm text-gray-700 transition-all duration-200 focus:outline-none"
            />
            <button
              @click="performSearch"
              class="absolute right-2 top-1/2 transform -translate-y-1/2 p-2 text-gray-500 hover:text-indigo-700 focus:outline-none"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </button>
          </div>
        </div>

        <!-- User + Icons with hover effects -->
        <div class="flex items-center space-x-5">
          <div class="flex items-center space-x-2 group cursor-pointer">
            <div class="relative">
              <div
                class="w-9 h-9 rounded-full overflow-hidden shadow-inner border border-gray-200"
              >
                <img
                  v-if="user?.image"
                  :src="getImageUrl(user.image)"
                  alt="User Image"
                  class="w-full h-full object-cover"
                />
                <div
                  v-else
                  class="w-full h-full bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center group-hover:from-indigo-50 group-hover:to-indigo-100 transition-colors"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="h-5 w-5 text-gray-600 group-hover:text-indigo-700"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                    />
                  </svg>
                </div>
              </div>
            </div>
            <template v-if="isAuthenticated">
              <div class="text-xs text-gray-500">{{ $t("nav.Welcome") }}</div>
              <router-link
                :to="{ name: 'userProfile' }"
                class="text-sm font-semibold text-indigo-700 hover:underline"
              >
                {{ user?.name || "User" }}
              </router-link>
              <button
                @click="logout"
                class="text-sm font-medium text-red-600 hover:text-red-700 transition-colors"
              >
                {{ $t("nav.Logout") }}
              </button>
            </template>

            <template v-else>
              <router-link to="/login" class="cursor-pointer group">
                <div class="text-xs text-gray-500">
                  {{ $t("nav.hello") }}, {{ $t("nav.guest") }}
                </div>
                <div
                  class="text-sm font-medium text-gray-700 group-hover:text-indigo-700 transition-colors"
                >
                  {{ $t("nav.Login") }} / {{ $t("nav.Register") }}
                </div>
              </router-link>
            </template>
          </div>

          <div class="flex items-center space-x-4">
            <router-link
              to="/wishlist"
              class="relative p-2 rounded-full hover:bg-gray-100 transition-colors group"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-5 w-5 text-gray-600 group-hover:text-indigo-700"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
                />
              </svg>
              <span
                class="absolute -top-1 -right-1 text-xs bg-indigo-700 text-white rounded-full w-5 h-5 flex items-center justify-center shadow-md"
              >
                {{ cartStore.wishlistItems.length }}
              </span>
            </router-link>
          </div>
          <!-- 🌍 Language Selector -->
          <div class="flex items-center space-x-2">
            <img
              v-if="selectedLang !== 'en'"
              src="/flags/en.png"
              alt="English"
              class="w-8 h-8 cursor-pointer hover:scale-110 transition-transform duration-200"
              @click="selectLang('en')"
            />
            <img
              v-if="selectedLang !== 'ar'"
              src="/flags/ar.png"
              alt="Arabic"
              class="w-8 h-8 cursor-pointer hover:scale-110 transition-transform duration-200"
              @click="selectLang('ar')"
            />
          </div>
        </div>
      </div>

      <!-- Bottom nav with animated underline -->
      <nav
        class="container mx-auto px-4 py-2.5 border-t border-gray-100 flex justify-between items-center text-sm"
      >
        <div class="flex space-x-8 font-medium text-gray-700">
          <div class="relative" ref="dropdownRef">
            <button
              @click="toggleDropdown"
              class="flex items-center space-x-1.5 cursor-pointer text-indigo-700 hover-button"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M4 6h16M4 12h16M4 18h16"
                />
              </svg>
              <span>{{ $t("nav.AllCategories") }}</span>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-4 w-4 transform transition-transform"
                :class="{ 'rotate-180': showDropdown }"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </button>

            <div
              ref="dropdownRef"
              v-show="showDropdown"
              :class="[
                'absolute top-full mt-2 w-64 bg-white border border-gray-200 rounded-md shadow-lg z-50',
                isRTL ? 'right-0' : 'left-0',
              ]"
            >
              <div class="max-h-64 overflow-y-auto">
                <router-link
                  v-for="cat in categories"
                  :key="cat.id"
                  :to="`/products?category_id=${cat.id}`"
                  class="flex items-center px-4 py-2 hover:bg-gray-50 transition"
                >
                  <img
                    :src="cat.image"
                    alt="Category"
                    class="w-8 h-8 object-cover rounded-full mr-3"
                  />
                  <span class="text-sm text-gray-700">{{ cat.name }}</span>
                </router-link>
              </div>
            </div>
          </div>

          <router-link
            v-for="link in navLinks"
            :key="link.name"
            :to="link.path"
            class="relative group hover:text-indigo-700 transition-colors"
          >
            {{ $t("nav." + link.key) }}
            <span v-if="link.dropdown" class="ml-0.5">▼</span>
            <span
              class="absolute bottom-0 left-0 w-0 h-0.5 bg-indigo-700 transition-all duration-300 group-hover:w-full"
            ></span>
          </router-link>
        </div>

        <router-link
          :to="{
            name: 'userProfile',
            query: { tab: 'items', openAddModal: '1' },
          }"
          class="inline-flex items-center px-5 py-2.5 bg-gradient-to-r from-teal-500 to-emerald-500 text-white text-sm font-semibold rounded-lg shadow-md hover:from-teal-600 hover:to-emerald-600 transition-all duration-300"
        >
          <svg
            class="h-5 w-5 mr-2"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M12 4v16m8-8H4"
            ></path>
          </svg>
          {{ $t("profile.addNewItem") }}
        </router-link>
      </nav>
    </div>

    <!-- Mobile menu (hidden by default) -->
    <div
      v-if="mobileMenuOpen"
      class="md:hidden bg-white border-t border-gray-200"
    >
      <div class="px-2 pt-2 pb-3 space-y-1">
        <router-link
          v-for="link in navLinks"
          :key="link.name"
          :to="link.path"
          class="block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-indigo-700 hover:bg-gray-50"
          @click="mobileMenuOpen = false"
        >
          {{ $t("nav." + link.key) }}
        </router-link>

        <!-- Mobile Categories Dropdown -->
        <div class="px-3 py-2">
          <div class="text-sm font-semibold text-gray-800 mb-2">
            {{ $t("nav.AllCategories") }}
          </div>
          <div class="space-y-2 max-h-64 overflow-y-auto">
            <router-link
              v-for="cat in categories"
              :key="cat.id"
              :to="`/products?category_id=${cat.id}`"
              @click="mobileMenuOpen = false"
              class="flex items-center px-3 py-2 rounded hover:bg-gray-100 transition"
            >
              <img
                :src="cat.image"
                alt="Category"
                class="w-7 h-7 rounded-full object-cover mr-3"
              />
              <span class="text-sm text-gray-700">{{ cat.name }}</span>
            </router-link>
          </div>
        </div>

        <div class="px-3 py-2">
          <router-link
            :to="{
              name: 'userProfile',
              query: { tab: 'items', openAddModal: '1' },
            }"
            @click="mobileMenuOpen = false"
            class="w-full inline-flex items-center justify-center px-5 py-2.5 bg-gradient-to-r from-teal-500 to-emerald-500 text-white text-sm font-semibold rounded-lg shadow-md hover:from-teal-600 hover:to-emerald-600 transition-all duration-300"
          >
            <svg
              class="h-5 w-5 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M12 4v16m8-8H4"
              ></path>
            </svg>
            {{ $t("profile.addNewItem") }}
          </router-link>
        </div>
        <!-- Language Switcher for Mobile -->
        <div class="px-3 py-2">
          <div class="text-sm font-semibold text-gray-800 mb-2">
            {{ $t("nav.language") }}
          </div>
          <div class="flex space-x-3">
            <img
              v-if="selectedLang !== 'en'"
              src="/flags/en.png"
              alt="English"
              class="w-6 h-6 cursor-pointer hover:scale-110 transition-transform duration-200"
              @click="
                () => {
                  selectLang('en');
                  mobileMenuOpen = false;
                }
              "
            />
            <img
              v-if="selectedLang !== 'ar'"
              src="/flags/ar.png"
              alt="Arabic"
              class="w-6 h-6 cursor-pointer hover:scale-110 transition-transform duration-200"
              @click="
                () => {
                  selectLang('ar');
                  mobileMenuOpen = false;
                }
              "
            />
          </div>
        </div>

        <template v-if="isAuthenticated && user?.name">
          <div class="px-4 py-3 border-t border-gray-200">
            <p class="text-sm text-gray-600">
              {{ $t("nav.Welcome") }},
              <router-link
                :to="{ name: 'userProfile' }"
                class="font-semibold text-indigo-700 hover:underline transition"
                @click="mobileMenuOpen = false"
              >
                {{ user.name }}
              </router-link>
            </p>
            <button
              @click="logout"
              class="mt-2 text-sm text-red-600 hover:text-red-700 font-medium transition-colors"
            >
              {{ $t("nav.Logout") }}
            </button>
          </div>
        </template>
        <template v-else>
          <router-link
            to="/login"
            class="block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-indigo-700 hover:bg-gray-50"
            @click="mobileMenuOpen = false"
          >
            Login / Register
          </router-link>
          <!-- Floating Chat Button -->
          <!-- <button
            @click="toggleChat"
            class="fixed bottom-6 right-6 bg-gradient-to-br from-purple-600 to-indigo-600 text-white p-4 rounded-full shadow-2xl hover:shadow-lg z-50 transition-all duration-300 hover:scale-105 group"
            :class="{ 'rotate-45': isChatOpen }"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-6 w-6 transform transition-transform duration-300 group-hover:scale-110"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                v-if="!isChatOpen"
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9 9 0 010-18c4.97 0 9 3.582 9 8z"
              />
              <path
                v-else
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button> -->
        </template>
      </div>
    </div>
  </header>
</template>

<script>
import { inject, ref, onMounted, computed, onBeforeUnmount } from "vue";
import useAuth from "@/composables/useAuth";
import { useRouter } from "vue-router";
import api from "@/api";
import { useRoute } from 'vue-router';

export default {
  name: "AppNavbar",
  setup() {
    const getImageUrl = (path) => {
      if (!path || typeof path !== "string") return "";
      if (path.startsWith("http")) return path;
      return `https://admin.from-malk.com/storage/${path}`;
    };
    const isChatOpen = ref(false);
    const toggleChat = () => {
      isChatOpen.value = !isChatOpen.value;
    };
    const settings = ref({
      logo: "",
      favicon: "",
      app_name: "",
    });

    const searchInput = ref("");
    const router = useRouter();

    const categories = ref([]);
    const fetchSettings = async () => {
      try {
        const response = await api.get("/v1/admin/settings");
        settings.value = response.data;

        document.title = response.data.app_name || "Website";

        const faviconLink =
          document.querySelector("link[rel~='icon']") ||
          document.createElement("link");
        faviconLink.rel = "icon";
        faviconLink.href = `https://admin.from-malk.com/storage/${response.data.favicon}`;
        document.head.appendChild(faviconLink);
      } catch (error) {
        console.error("Failed to load settings:", error);
      }
    };
    const fetchCategories = async () => {
      try {
        const response = await api.get("/v1/categories");
        categories.value = response.data.map((cat) => ({
          id: cat.id,
          name: cat.name,
          image: `https://admin.from-malk.com/uploads/${cat.image}`,
        }));
      } catch (error) {
        console.error("Failed to load categories:", error);
      }
    };
const route = useRoute();
  const isProductPage = computed(() => route.path === '/products');
  const isHomePage = computed(() => route.path === '/');
    onMounted(() => {

      
      fetchCategories();
      fetchSettings();
      document.addEventListener("click", handleClickOutside);
    });
    onBeforeUnmount(() => {
      document.removeEventListener("click", handleClickOutside);
    });

    const performSearch = () => {
      if (searchInput.value.trim()) {
        router.push({
          path: "/products",
          query: { q: searchInput.value.trim() },
        });
      }
    };
    const cartStore = inject("cartStore");
    const mobileMenuOpen = ref(false);
    const { isAuthenticated, user, logout } = useAuth();
    // 🌍 Language Logic
    const selectedLang = ref(localStorage.getItem("lang_code") || "ar");
    const languages = [
      { code: "en", label: "English" },
      { code: "ar", label: "العربية" },
    ];
    const selectLang = (code) => {
      localStorage.setItem("lang_code", code);
      selectedLang.value = code;
      window.location.reload(); // Reload to reflect change if needed
    };
    const dropdownRef = ref(null);
    const showDropdown = ref(false);
    const handleClickOutside = (event) => {
      if (dropdownRef.value && !dropdownRef.value.contains(event.target)) {
        showDropdown.value = false;
      }
    };
    const toggleDropdown = () => {
      showDropdown.value = !showDropdown.value;
    };
    const isRTL = computed(() => selectedLang.value === "ar");
    return {
      getImageUrl,
      cartStore,
      mobileMenuOpen,
      isAuthenticated,
      user,
      logout,
      selectedLang,
      languages,
      selectLang,
      searchInput,
      performSearch,
      categories,
      isChatOpen,
      toggleChat,
      isRTL,
      showDropdown,
      settings,
      toggleDropdown,
      dropdownRef,
      isProductPage,
      isHomePage
    };
  },

  data() {
    return {
      navLinks: [
        { key: "home", path: "/", dropdown: false },
        { key: "products", path: "/products", dropdown: false },

        { key: "contact", path: "/contact", dropdown: false },
        { key: "commission", path: "/commission", dropdown: false },
      ],
    };
  },
};
</script>
